/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Site-wide announcement lightbox — pops on first visit per browser session
 * to let customers know Teeodore is in recovery and production is paused.
 *
 * - Persistence: sessionStorage so it shows once per browsing session, not
 *   every route change. Clearing the tab / re-opening the site re-shows it.
 * - Dismiss: X, Escape, backdrop click, or the "Future Order" CTA (which
 *   also smooth-scrolls the custom request section).
 */
const STORAGE_KEY = 'sh_recovery_notice_v1_dismissed';

const RecoveryAnnouncementModal = ({ onFutureOrder }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip on non-home routes (admin, tracker etc.)
    if (window.location.pathname !== '/') return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      /* private mode — just show */
    }
    // Slight delay so the site paints first, then the lightbox lands.
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    setOpen(false);
  };

  const handleFutureOrder = () => {
    dismiss();
    // Give the modal a beat to close before scrolling.
    setTimeout(() => {
      if (typeof onFutureOrder === 'function') onFutureOrder();
    }, 200);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="recovery-notice-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 sm:py-10"
          onClick={dismiss}
        >
          <motion.div
            data-testid="recovery-notice-modal"
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            className="relative w-full max-w-3xl bg-[#FFF7EE] text-black rounded-3xl border-4 border-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="recovery-notice-title"
          >
            {/* Close */}
            <button
              type="button"
              data-testid="recovery-notice-close"
              onClick={dismiss}
              aria-label="Close announcement"
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black text-white text-xl font-black hover:bg-brand-orange hover:text-black transition-colors z-10 flex items-center justify-center"
            >
              ×
            </button>

            {/* Header band */}
            <div className="bg-black text-white px-6 sm:px-10 pt-8 pb-6 relative">
              <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
                <img
                  src="/brand/teeodore-sling.png"
                  alt="Teeodore with arm in sling"
                  className="w-32 sm:w-40 shrink-0 drop-shadow-[0_8px_24px_rgba(255,102,0,0.45)]"
                  data-testid="recovery-notice-mascot"
                />
                <div className="text-center sm:text-left flex-1">
                  <div className="inline-block px-3 py-1 rounded-full bg-brand-orange text-black font-display font-black text-[10px] tracking-[0.32em] uppercase mb-3">
                    Studio Update
                  </div>
                  <h2
                    id="recovery-notice-title"
                    className="font-display font-black leading-[0.95] text-3xl sm:text-4xl lg:text-5xl"
                  >
                    Teeodore is Taking a<br className="hidden sm:block" />
                    <span className="text-brand-orange"> Quick Pit Stop</span>
                  </h2>
                  <p className="mt-3 font-display font-black uppercase tracking-[0.24em] text-sm text-white/80">
                    We'll Be Back Soon!
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 sm:px-10 py-7 sm:py-8 font-body text-[15px] leading-relaxed space-y-5">
              <p>
                Hey friends! Teeodore wanted everyone to know he'll be having
                {' '}<strong>shoulder surgery on August 6, 2026</strong>, and he'll
                be taking some time to recover.
              </p>
              <p>
                To make sure he heals properly, <strong>Shirt Happens Studio
                will temporarily pause shirt production for approximately
                6–8 weeks.</strong>
              </p>

              <div className="rounded-2xl bg-brand-orange/15 border-2 border-brand-orange/40 px-5 py-4">
                <div className="font-display font-black uppercase tracking-[0.2em] text-sm text-black mb-2">
                  💪 The Good News?
                </div>
                <p className="text-[14.5px]">
                  This is only temporary! We're looking forward to returning
                  better than ever and can't wait to help bring your ideas to
                  life again.
                </p>
              </div>

              <div>
                <div className="font-display font-black uppercase tracking-[0.2em] text-sm text-black mb-3">
                  During Recovery
                </div>
                <ul className="space-y-2 text-[14.5px]">
                  <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span><span>New shirt production will be temporarily paused.</span></li>
                  <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span><span>We'll still be responding to messages and planning upcoming projects.</span></li>
                  <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span><span>Want to place an order for after our return? Tap the button below to submit your request. We'll contact you to discuss your project and reserve your spot on our production schedule.</span></li>
                </ul>
              </div>

              <div className="rounded-2xl bg-black text-white px-5 py-4">
                <div className="font-display font-black uppercase tracking-[0.2em] text-xs text-brand-orange mb-2">
                  📅 Expected Return
                </div>
                <p className="text-[14.5px] text-white/90">
                  <strong>Late September to Early October 2026</strong>
                  {' '}(pending recovery and medical clearance).
                </p>
              </div>

              <p>
                Thank you for your patience, your understanding, and your
                continued support of Shirt Happens Studio. It truly means the
                world to us.
              </p>
              <p className="font-display font-black uppercase tracking-[0.18em] text-black">
                We can't wait to get back to doing what we love…<br />
                <span className="text-brand-orange">Making Shirt Happen!</span>
              </p>

              {/* CTAs */}
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  data-testid="recovery-notice-future-order"
                  onClick={handleFutureOrder}
                  className="flex-1 px-6 py-4 rounded-full bg-brand-orange text-black font-display font-black tracking-[0.18em] uppercase text-sm hover:bg-black hover:text-brand-orange border-2 border-black transition-colors"
                >
                  Reserve a Future Order →
                </button>
                <button
                  type="button"
                  data-testid="recovery-notice-dismiss"
                  onClick={dismiss}
                  className="px-6 py-4 rounded-full bg-transparent text-black/80 font-display font-black tracking-[0.18em] uppercase text-xs border-2 border-black/30 hover:border-black hover:text-black transition-colors"
                >
                  Got it, thanks
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecoveryAnnouncementModal;
